import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ModeEditIcon from '@mui/icons-material/ModeEdit';
import UndoIcon from '@mui/icons-material/Undo';
import { IconButton, Stack, Typography } from '@mui/material';
import { cyan, red } from '@mui/material/colors';

import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import WaveUIAccordion from '@/shared/components/widgets/WaveUIAccordion';
import countries from '@/shared/configs/countries.json';
import OrderCustomer from '@/shared/types/order/orderCustomer.type';

type Reason = 'billing' | 'delivery';

type AddressExpandablePanelProps = {
  customer: OrderCustomer;
  selectedCustomerSnapshot: OrderCustomer;
  title: string;
  reason: Reason;
  customerAddressFormErrors: Record<string, string>;
  onValidateCustomerAddressForm: (
    fieldName: string,
    useSnapshot?: boolean,
  ) => void;
  setSelectedCustomer: (value: OrderCustomer[]) => void;
};

const AddressExpandablePanel: FC<AddressExpandablePanelProps> = ({
  customer,
  selectedCustomerSnapshot,
  title,
  reason,
  customerAddressFormErrors,
  onValidateCustomerAddressForm,
  setSelectedCustomer,
}) => {
  const { t } = useTranslation(['customers', 'common']);

  const [formExpanded, setFormExpanded] = useState(false);

  const customerAddressConfig = {
    billing: {
      address: customer?.billing_address,
      address2: customer?.billing_address2,
      city: customer?.billing_city,
      country: customer?.billing_country,
      first_name: customer?.billing_first_name,
      last_name: customer?.billing_last_name,
      phone: customer?.billing_phone,
      postcode: customer?.billing_postcode,
      state: customer?.billing_state,
      email_address: customer?.billing_email_address,
    },
    delivery: {
      address: customer?.delivery_address,
      address2: customer?.delivery_address2,
      city: customer?.delivery_city,
      country: customer?.delivery_country,
      first_name: customer?.delivery_first_name,
      last_name: customer?.delivery_last_name,
      phone: customer?.delivery_phone,
      postcode: customer?.delivery_postcode,
      email_address: customer?.delivery_email_address,
      state: customer?.delivery_state,
    },
  };

  const getFieldData = (
    field: keyof (typeof customerAddressConfig)[Reason],
  ) => {
    return {
      name: `${reason}_${field}`,
      value: customerAddressConfig[reason][field] || '',
      isDifferentFromSnapshot:
        customerAddressConfig[reason][field] !==
        selectedCustomerSnapshot[`${reason}_${field}`],
      styles: {
        backgroundColor:
          customerAddressConfig[reason][field] !==
          selectedCustomerSnapshot[`${reason}_${field}`]
            ? cyan[50]
            : 'inherit',
      },
    };
  };

  const getCountriesOptions = () => {
    return (
      countries
        .find(
          (country) =>
            country.code ===
            (reason === 'billing'
              ? customer.billing_country
              : customer.delivery_country),
        )
        ?.provinces.map((province) => ({
          label: province.name,
          value: province.code,
        })) || []
    );
  };

  const getFieldColumn = (
    field: ReactNode,
    fieldName: keyof (typeof customerAddressConfig)[Reason],
  ) => {
    return (
      <Stack direction='row' gap={2} alignItems='center'>
        {field}
        {getFieldData(fieldName).isDifferentFromSnapshot ? (
          <IconButton
            tabIndex={-1}
            size='small'
            onClick={() => {
              setSelectedCustomer([
                {
                  ...customer,
                  [`${reason}_${fieldName}`]:
                    selectedCustomerSnapshot[`${reason}_${fieldName}`],
                },
              ]);
              onValidateCustomerAddressForm(`${reason}_${fieldName}`, true);
            }}
          >
            <UndoIcon fontSize='inherit' />
          </IconButton>
        ) : null}
      </Stack>
    );
  };

  return (
    <WaveUIAccordion
      summaryProps={{
        sx: {
          '*': {
            color: Object.values(customerAddressFormErrors).some(
              (item) => item !== '',
            )
              ? red[400]
              : 'inherit',
          },
        },
        expandIcon: null,
      }}
      title={
        formExpanded ? (
          <Typography variant='body2'>{title}</Typography>
        ) : (
          <div>
            <Typography variant='body2'>{title}</Typography>

            {!customerAddressConfig[reason].address &&
              !customerAddressConfig[reason].address2 &&
              !customerAddressConfig[reason].city &&
              !customerAddressConfig[reason].state &&
              !customerAddressConfig[reason].postcode && (
                <Typography variant='body2' color='text.secondary'>
                  {t('customers:addressIsNotAvailable')}
                </Typography>
              )}

            <Stack spacing={1} direction='row'>
              <Typography variant='body2' color='text.secondary'>
                {customerAddressConfig[reason].address}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {customerAddressConfig[reason].address2}
              </Typography>
            </Stack>

            <Typography variant='body2' color='text.secondary'>
              {customerAddressConfig[reason].city}
            </Typography>

            <Stack spacing={1} direction='row'>
              <Typography variant='body2' color='text.secondary'>
                {customerAddressConfig[reason].state}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {customerAddressConfig[reason].postcode}
              </Typography>
            </Stack>
          </div>
        )
      }
      actions={[
        {
          label: t('common:edit'),
          icon: <ModeEditIcon sx={{ width: 16, height: 16 }} />,
          onClick: () => {
            setFormExpanded((prev) => !prev);
          },
        },
      ]}
      elevation={0}
      expanded={formExpanded}
    >
      <WaveUIForm_V2
        fields={[
          {
            columns: 6,
            field_type: FieldType.Text,
            field_props: {
              name: getFieldData('first_name').name,
              value: getFieldData('first_name').value,
              sx: getFieldData('first_name').styles,
              label: t('customers:first_name'),
              placeholder: t('customers:first_name'),
              required: true,
              error: !!customerAddressFormErrors[`${reason}_first_name`],
              helperText: customerAddressFormErrors[`${reason}_first_name`],
              onBlur: () =>
                onValidateCustomerAddressForm(`${reason}_first_name`),
            },
            columnRenderer: (field) => getFieldColumn(field, 'first_name'),
          },
          {
            columns: 6,
            field_type: FieldType.Text,
            field_props: {
              name: getFieldData('last_name').name,
              value: getFieldData('last_name').value,
              sx: getFieldData('last_name').styles,
              label: t('customers:last_name'),
              placeholder: t('customers:last_name'),
              required: true,
              error: !!customerAddressFormErrors[`${reason}_last_name`],
              helperText: customerAddressFormErrors[`${reason}_last_name`],
              onBlur: () =>
                onValidateCustomerAddressForm(`${reason}_last_name`),
            },
            columnRenderer: (field) => getFieldColumn(field, 'last_name'),
          },
          {
            field_type: FieldType.Text,
            field_props: {
              name: getFieldData('email_address').name,
              value: getFieldData('email_address').value,
              sx: getFieldData('email_address').styles,
              label: t('customers:email_address'),
              placeholder: t('customers:email_address'),
              required: true,
              error: !!customerAddressFormErrors[`${reason}_email_address`],
              helperText: customerAddressFormErrors[`${reason}_email_address`],
              onBlur: () =>
                onValidateCustomerAddressForm(`${reason}_email_address`),
            },
            columnRenderer: (field) => getFieldColumn(field, 'email_address'),
          },
          {
            field_type: FieldType.Text,
            field_props: {
              name: getFieldData('phone').name,
              value: getFieldData('phone').value,
              sx: getFieldData('phone').styles,
              label: t('customers:phone'),
              placeholder: t('customers:phone'),
              required: true,
              error: !!customerAddressFormErrors[`${reason}_phone`],
              helperText: customerAddressFormErrors[`${reason}_phone`],
              onBlur: () => onValidateCustomerAddressForm(`${reason}_phone`),
            },
            columnRenderer: (field) => getFieldColumn(field, 'phone'),
          },
          {
            field_type: FieldType.Select,
            field_props: {
              name: getFieldData('country').name,
              value: getFieldData('country').value,
              sx: getFieldData('country').styles,
              options: countries.map((country) => ({
                label: country.country,
                value: country.code,
              })),
              label: t('customers:country'),
              placeholder: t('customers:country'),
              required: true,
              error: !!customerAddressFormErrors[`${reason}_country`],
              helperText: customerAddressFormErrors[`${reason}_country`],
              onBlur: () => onValidateCustomerAddressForm(`${reason}_country`),
            },
            columnRenderer: (field) => getFieldColumn(field, 'country'),
          },
          {
            field_type: FieldType.Text,
            field_props: {
              name: getFieldData('address').name,
              value: getFieldData('address').value,
              sx: getFieldData('address').styles,
              label: t('customers:address'),
              placeholder: t('customers:address'),
              required: true,
              error: !!customerAddressFormErrors[`${reason}_address`],
              helperText: customerAddressFormErrors[`${reason}_address`],
              onBlur: () => onValidateCustomerAddressForm(`${reason}_address`),
            },
            columnRenderer: (field) => getFieldColumn(field, 'address'),
          },
          {
            field_type: FieldType.Text,
            field_props: {
              name: getFieldData('address2').name,
              value: getFieldData('address2').value,
              sx: getFieldData('address2').styles,
              label: t('customers:address2'),
              placeholder: t('customers:address2'),
            },
            columnRenderer: (field) => getFieldColumn(field, 'address2'),
          },
          {
            columns: 6,
            field_type: FieldType.Text,
            field_props: {
              name: getFieldData('city').name,
              value: getFieldData('city').value,
              sx: getFieldData('city').styles,
              label: t('customers:city'),
              placeholder: t('customers:city'),
              required: true,
              error: !!customerAddressFormErrors[`${reason}_city`],
              helperText: customerAddressFormErrors[`${reason}_city`],
              onBlur: () => onValidateCustomerAddressForm(`${reason}_city`),
            },
            columnRenderer: (field) => getFieldColumn(field, 'city'),
          },
          {
            columns: 6,
            field_type: FieldType.Select,
            field_props: {
              name: getFieldData('state').name,
              value: getFieldData('state').value,
              sx: getFieldData('state').styles,
              options: getCountriesOptions(),
              label: t('customers:state'),
              placeholder: t('customers:state'),
              required: true,
              error: !!customerAddressFormErrors[`${reason}_state`],
              helperText: customerAddressFormErrors[`${reason}_state`],
              onBlur: () => onValidateCustomerAddressForm(`${reason}_state`),
            },
            columnRenderer: (field) => getFieldColumn(field, 'state'),
          },
          {
            field_type: FieldType.Text,
            field_props: {
              name: getFieldData('postcode').name,
              value: getFieldData('postcode').value,
              sx: getFieldData('postcode').styles,
              label: t('customers:postcode'),
              placeholder: t('customers:postcode'),
              required: true,
              error: !!customerAddressFormErrors[`${reason}_postcode`],
              helperText: customerAddressFormErrors[`${reason}_postcode`],
              onBlur: () => onValidateCustomerAddressForm(`${reason}_postcode`),
            },
            columnRenderer: (field) => getFieldColumn(field, 'postcode'),
          },
        ]}
        getFieldsValues={(value) => {
          if (value[`${reason}_country`]) {
            const country = countries.find(
              (country) => country.code === value[`${reason}_country`],
            );

            value[`${reason}_state`] = country?.provinces[0].code;
          }

          setSelectedCustomer([{ ...customer, ...value }]);
        }}
      />
    </WaveUIAccordion>
  );
};

export default AddressExpandablePanel;
